/**
 * The order plan's questions (XC-INV2PO answering note §4), through the gateway to agent_service,
 * which takes the tenant from the signed-in user's token. Every reply is parsed before the Inbox sees
 * it (a reply that fails is refused whole). A refused answer (409) carries his words: the card shows
 * them as they come.
 */

import { config } from '@/config/environment';
import { getAuthToken } from '@/contexts/AuthContext';
import {
  AnswerResultSchema,
  OrderQuestionsSchema,
  UndoResultSchema,
  type AnswerResult,
  type HeldCountRow,
  type LineCount,
  type OrderQuestions,
  type RecipeComponent,
  type UndoResult,
} from '@/types/inv2po/orderQuestions';
import axios, { isAxiosError } from 'axios';
import type { z } from 'zod';

export const ORDER_QUESTIONS_PATH = '/api/v1/inv2po/questions';

const api = axios.create({
  baseURL: config.GATEWAY_URL,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((reqConfig) => {
  const token = getAuthToken();
  if (token) {
    reqConfig.headers.Authorization = `Bearer ${token}`;
  }
  return reqConfig;
});

/** The reply did not hold to the contract: nothing of it is shown. */
export class OrderQuestionsContractError extends Error {
  constructor(public readonly issues: string[]) {
    super(`order questions failed their contract: ${issues.join('; ')}`);
    this.name = 'OrderQuestionsContractError';
  }
}

/** An answer or Undo the server refused or could not take, in words for the card. */
export class OrderAnswerError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'OrderAnswerError';
  }
}

function parsed<S extends z.ZodTypeAny>(schema: S, data: unknown): z.infer<S> {
  const out = schema.safeParse(data);
  if (!out.success) {
    throw new OrderQuestionsContractError(
      out.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`),
    );
  }
  return out.data;
}

function refused(err: unknown): never {
  if (isAxiosError(err)) {
    const status = err.response?.status;
    const reason = (err.response?.data as { reason?: unknown } | undefined)?.reason;
    if (status === 409 && typeof reason === 'string' && reason) throw new OrderAnswerError(reason);
    if (status === 404) throw new OrderAnswerError('This question is no longer open. Refresh to see the rest.');
    if (status === 403) throw new OrderAnswerError('Only an owner or admin of this workspace can answer.');
  }
  throw new OrderAnswerError('Your answer could not be saved just now. Try again in a minute.');
}

export async function fetchOrderQuestions(): Promise<OrderQuestions> {
  const { data } = await api.get<unknown>(ORDER_QUESTIONS_PATH);
  return parsed(OrderQuestionsSchema, data);
}

/**
 * His answer: a card's value, for "already made?" the line numbers he ticked, for "ask me again"
 * (the pallets card) in how many weeks, for "count it in my plan" his count of each line (AW-144 slice 3), and for
 * a held recipe every material of a case with its amount, and for an order held on its lines his products and counts.
 */
export async function answerOrderQuestion(
  questionId: string,
  value: string,
  lineNos: number[] = [],
  weeks?: number,
  counts?: LineCount[],
  components?: RecipeComponent[],
  products?: HeldCountRow[],
): Promise<AnswerResult> {
  try {
    const body: Record<string, unknown> = { value, line_nos: lineNos };
    if (weeks !== undefined) body.weeks = weeks;
    if (counts !== undefined) body.counts = counts;
    if (components !== undefined) body.components = components;
    if (products !== undefined) body.products = products;
    const { data } = await api.post<unknown>(
      `${ORDER_QUESTIONS_PATH}/${encodeURIComponent(questionId)}/answer`,
      body,
    );
    return parsed(AnswerResultSchema, data);
  } catch (err) {
    if (err instanceof OrderQuestionsContractError) throw err;
    return refused(err);
  }
}

/** His Undo of one answer: the question is open again. */
export async function undoOrderAnswer(answerId: string): Promise<UndoResult> {
  try {
    const { data } = await api.post<unknown>(
      `/api/v1/inv2po/answers/${encodeURIComponent(answerId)}/undo`,
    );
    return parsed(UndoResultSchema, data);
  } catch (err) {
    if (err instanceof OrderQuestionsContractError) throw err;
    return refused(err);
  }
}
